/* eslint-disable no-unused-vars */
import {WalletPlatform} from '@/enums/WalletPlatform'
import {
  RejectedByUser,
  SdkNotInitializedError,
} from '@/model/wallets/types/WalletErrors'
import {TWalletNetwork} from '@/model/wallets/types/WalletNetwork'
import {NeoXChainHelper, TNeoXChainConfig} from '@/helpers/NeoXChainHelper'
import {ConnectionStrategy} from './ConnectionStrategy'
import {
  neoLineNetworkLabel,
  neoLineNetworkNameLabel,
  validateNeoLineAddressNetwork,
  WrongNeoLineNetworkError,
} from './NeolineNetworkValidation'

export type NeolineNeoXEthereumProviderContext = {
  type: 'neoline-neox-ethereum'
  provider: NeoLineEthereumProvider
  address: string | null
}

type NeoLineEthereumProvider = {
  request: (args: {method: string; params?: unknown[]}) => Promise<unknown>
  on?: (event: string, handler: (...args: unknown[]) => void) => void
  removeListener?: (
    event: string,
    handler: (...args: unknown[]) => void
  ) => void
}

export const isNeolineNeoXEthereumProvider = (
  provider: unknown
): provider is NeolineNeoXEthereumProviderContext =>
  typeof provider === 'object' &&
  provider !== null &&
  (provider as NeolineNeoXEthereumProviderContext).type ===
    'neoline-neox-ethereum'

const NEOX_WINDOW_GLOBALS = ['NEOLineNeoX', 'NEOLineNEOX'] as const

const requestNeoLineDapiNetworkSwitch = async (
  chainId: number
): Promise<boolean> => {
  if (typeof window === 'undefined') return false

  for (const globalKey of ['NEOLineNEOX', 'NEOLineN3'] as const) {
    const dapi = (window as any)[globalKey]

    if (typeof dapi?.Init !== 'function') continue

    try {
      const sdk = new dapi.Init()

      if (typeof sdk?.switchWalletNetwork !== 'function') continue

      await sdk.switchWalletNetwork({chainId})

      return true
    } catch (error) {
      console.warn(error)
    }
  }

  return false
}

type NeoLineDapiNetworkReport = {
  chainId: number | null
  networkName: string | null
}

const readNeoLineDapiNetwork = async (
  globalKeys: readonly string[] = ['NEOLineNEOX', 'NEOLineN3']
): Promise<NeoLineDapiNetworkReport> => {
  if (typeof window === 'undefined') return {chainId: null, networkName: null}

  for (const globalKey of globalKeys) {
    const dapi = (window as any)[globalKey]

    if (typeof dapi?.Init !== 'function') continue

    try {
      const sdk = new dapi.Init()

      if (typeof sdk?.getNetworks !== 'function') continue

      const networks = await sdk.getNetworks()
      const chainId = NeoXChainHelper.parseChainId(
        networks?.chainId ?? networks?.chainid
      )

      return {
        chainId: chainId !== null && chainId >= 0 ? chainId : null,
        networkName:
          networks?.defaultNetwork ?? networks?.defaultnetwork ?? null,
      }
    } catch (error) {
      console.warn('[NeoLine][network] dAPI getNetworks failed', {
        via: globalKey,
        error,
      })
    }
  }

  return {chainId: null, networkName: null}
}

/** Where the wallet is, asked over the Neo X dAPI alone — the N3 one is never touched. */
export const readNeoLineNeoXDapiNetwork = (): Promise<NeoLineDapiNetworkReport> =>
  readNeoLineDapiNetwork(['NEOLineNEOX'])

/**
 * The chain the injected provider is on. `eth_chainId` is a plain read: it opens
 * no wallet window, whichever network NeoLine happens to be sitting on.
 */
export const readNeoLineEvmChainId = async (): Promise<number | null> => {
  const provider = resolveNeoLineEthereumProvider()

  if (!provider) return null

  try {
    return NeoXChainHelper.parseChainId(
      await provider.request({method: 'eth_chainId'})
    )
  } catch {
    return null
  }
}

const NEO_N3_CHAIN_IDS = [3, 6]

const NEO_N3_LABELS = ['Neo N3 Mainnet', 'Neo N3 Testnet']

const reportsNeoN3 = (report: NeoLineDapiNetworkReport): boolean => {
  if (report.chainId !== null) {
    return NEO_N3_CHAIN_IDS.includes(report.chainId)
  }

  return NEO_N3_LABELS.includes(
    neoLineNetworkNameLabel(report.networkName) ?? ''
  )
}

const isNeoLineFlaggedProvider = (candidate: any): boolean =>
  !!candidate &&
  typeof candidate === 'object' &&
  typeof candidate.request === 'function' &&
  (candidate.isNEOLine === true || candidate.isNeoLine === true)

export const resolveNeoLineEthereumProvider = (): NeoLineEthereumProvider | null => {
  if (typeof window === 'undefined') return null

  for (const key of NEOX_WINDOW_GLOBALS) {
    const candidate = (window as any)[key]

    if (
      candidate &&
      typeof candidate === 'object' &&
      typeof (candidate as NeoLineEthereumProvider).request === 'function'
    ) {
      return candidate as NeoLineEthereumProvider
    }
  }

  const ethereum = (window as any).ethereum

  if (Array.isArray(ethereum?.providers)) {
    const flagged = ethereum.providers.find(isNeoLineFlaggedProvider)

    if (flagged) return flagged as NeoLineEthereumProvider
  }

  return isNeoLineFlaggedProvider(ethereum)
    ? (ethereum as NeoLineEthereumProvider)
    : null
}

export const isNeoLineEthereumProviderAvailable = () =>
  !!resolveNeoLineEthereumProvider()

export class NeolineNeoXEthereumConnection implements ConnectionStrategy {
  private ethProvider: NeoLineEthereumProvider | null = null
  private address: string | null = null
  private onAnotherNetwork = false
  private detachListeners?: () => void

  constructor(private readonly network: TWalletNetwork) {}

  async init(onDeleteAdapter?: () => void) {
    this.detachListeners?.()
    this.ethProvider = await this.waitForProvider(15000)

    if (!this.ethProvider) {
      throw SdkNotInitializedError(WalletPlatform.NEOLINE)
    }

    this.attachListeners(onDeleteAdapter)
  }

  isInstalled() {
    return !!this.ethProvider || isNeoLineEthereumProviderAvailable()
  }

  async connect() {
    const provider = this.ethProvider ?? resolveNeoLineEthereumProvider()

    if (!provider) throw SdkNotInitializedError(WalletPlatform.NEOLINE)

    this.ethProvider = provider

    await this.prepareExpectedChain(provider)

    let accounts: string[]

    try {
      accounts = (await provider.request({
        method: 'eth_requestAccounts',
      })) as string[]
    } catch (error) {
      if (error?.code === 4001) {
        throw RejectedByUser
      }

      throw error
    }

    const address = accounts?.[0] ?? null
    if (!address) throw RejectedByUser

    validateNeoLineAddressNetwork(address, 'neoX')

    await this.ensureExpectedChain(provider)

    this.address = address
    this.onAnotherNetwork = false

    return {
      type: 'neoline-neox-ethereum',
      provider,
      address: this.address,
    } as NeolineNeoXEthereumProviderContext
  }

  getConnectedProvider(): NeolineNeoXEthereumProviderContext | null {
    if (!this.ethProvider) return null

    return {
      type: 'neoline-neox-ethereum',
      provider: this.ethProvider,
      address: this.address,
    }
  }

  async disconnect() {
    this.detachListeners?.()
    this.detachListeners = undefined
    this.address = null
    this.onAnotherNetwork = false
  }

  async ensureNetwork(): Promise<void> {
    const provider = this.ethProvider ?? resolveNeoLineEthereumProvider()

    if (!provider) throw SdkNotInitializedError(WalletPlatform.NEOLINE)

    this.ethProvider = provider

    await this.ensureExpectedChain(provider)
  }

  restoreBlockedByNetwork() {
    return this.onAnotherNetwork
  }

  async restoreSession() {
    const provider = this.ethProvider ?? resolveNeoLineEthereumProvider()

    this.onAnotherNetwork = false

    if (!provider) {
      this.address = null
      return
    }

    this.ethProvider = provider

    try {
      if (!(await this.isOnExpectedChain(provider))) {
        this.address = null
        this.onAnotherNetwork = true
        return
      }

      const accounts = (await provider.request({
        method: 'eth_accounts',
      })) as string[]

      const address = accounts?.[0] ?? null

      if (address) {
        validateNeoLineAddressNetwork(address, 'neoX')
      }

      this.address = address
    } catch {
      this.address = null
    }
  }

  private expectedChainId(): number | null {
    try {
      return NeoXChainHelper.chainId(this.network)
    } catch (error) {
      console.error('[NeoLine] Neo X chain is not configured', error)

      return null
    }
  }

  private async readChainId(
    provider: NeoLineEthereumProvider
  ): Promise<number | null> {
    try {
      return NeoXChainHelper.parseChainId(
        await provider.request({method: 'eth_chainId'})
      )
    } catch {
      return null
    }
  }

  private async isOnExpectedChain(
    provider: NeoLineEthereumProvider
  ): Promise<boolean> {
    const expected = this.expectedChainId()

    if (expected === null) return false

    return (await this.readChainId(provider)) === expected
  }

  private async prepareExpectedChain(provider: NeoLineEthereumProvider) {
    const expected = this.expectedChainId()

    if (expected === null) return

    const report = await readNeoLineDapiNetwork()
    const current = await this.readChainId(provider)

    if (this.isSettled(report, current, expected)) return
    if (reportsNeoN3(report)) {
      await requestNeoLineDapiNetworkSwitch(expected)

      if (await this.waitForSettled(provider, expected, 3000)) return
    }

    try {
      await provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{chainId: NeoXChainHelper.chainIdHex(this.network)}],
      })
    } catch (error) {
      if (error?.code === 4001) throw RejectedByUser

      await requestNeoLineDapiNetworkSwitch(expected)
    }
  }

  private async ensureExpectedChain(provider: NeoLineEthereumProvider) {
    const expected = this.expectedChainId()

    if (expected === null) throw WrongNeoLineNetworkError('neoX')

    const report = await readNeoLineDapiNetwork()
    const evmChainId = await this.readChainId(provider)
    const onNeoN3 = reportsNeoN3(report)

    if (this.isSettled(report, evmChainId, expected)) return

    const detected =
      neoLineNetworkLabel(report.chainId) ??
      neoLineNetworkNameLabel(report.networkName) ??
      neoLineNetworkLabel(evmChainId)
    const chain = NeoXChainHelper.chainConfig(this.network)

    if (onNeoN3) {
      await requestNeoLineDapiNetworkSwitch(expected)
    }

    if (!(await this.waitForSettled(provider, expected, 3000))) {
      await this.switchThroughEvm(provider, chain)
    }

    if (!(await this.waitForSettled(provider, expected, 3000))) {
      console.warn(
        '[NeoLine][network] Neo X still off chain, trying the dAPI',
        {
          chainIdExpected: expected,
        }
      )

      await requestNeoLineDapiNetworkSwitch(expected)
    }

    const ok = await this.waitForSettled(provider, expected, 3000)
    if (!ok) throw WrongNeoLineNetworkError('neoX', detected)
  }

  private async switchThroughEvm(
    provider: NeoLineEthereumProvider,
    chain: TNeoXChainConfig
  ) {
    try {
      await provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{chainId: chain.chainIdHex}],
      })
    } catch (error) {
      if (error?.code === 4001) throw RejectedByUser
    }
  }

  private isSettled(
    report: NeoLineDapiNetworkReport,
    evmChainId: number | null,
    expected: number
  ): boolean {
    if (reportsNeoN3(report)) return false

    if (report.chainId !== null) return report.chainId === expected

    return evmChainId === expected
  }

  private async waitForSettled(
    provider: NeoLineEthereumProvider,
    expected: number,
    timeoutMs: number
  ): Promise<boolean> {
    const start = Date.now()

    for (;;) {
      const report = await readNeoLineDapiNetwork()
      const evmChainId = await this.readChainId(provider)

      if (this.isSettled(report, evmChainId, expected)) return true

      if (Date.now() - start >= timeoutMs) return false

      await new Promise(resolve => setTimeout(resolve, 200))
    }
  }

  private async addChain(
    provider: NeoLineEthereumProvider,
    chain: TNeoXChainConfig
  ): Promise<boolean> {
    if (chain.rpcUrls.length === 0) return false

    try {
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: chain.chainIdHex,
            chainName: chain.chainName,
            rpcUrls: chain.rpcUrls,
            nativeCurrency: chain.nativeCurrency,
            ...(chain.blockExplorerUrls.length > 0
              ? {blockExplorerUrls: chain.blockExplorerUrls}
              : {}),
          },
        ],
      })

      return true
    } catch (error) {
      if (error?.code === 4001) throw RejectedByUser

      console.warn('[NeoLine] could not add the Neo X chain', error)

      return false
    }
  }

  private attachListeners(onDeleteAdapter?: () => void) {
    const provider = this.ethProvider

    if (!provider?.on) return

    const onAccountsChanged = () => {
      this.address = null
      onDeleteAdapter?.()
    }

    const onChainChanged = (...args: unknown[]) => {
      const chainId = NeoXChainHelper.parseChainId(args?.[0])

      if (chainId !== null && chainId === this.expectedChainId()) return

      this.address = null
      onDeleteAdapter?.()
    }

    provider.on('accountsChanged', onAccountsChanged)
    provider.on('chainChanged', onChainChanged)

    this.detachListeners = () => {
      provider.removeListener?.('accountsChanged', onAccountsChanged)
      provider.removeListener?.('chainChanged', onChainChanged)
    }
  }

  private waitForProvider(
    timeoutMs: number
  ): Promise<NeoLineEthereumProvider | null> {
    const existing = resolveNeoLineEthereumProvider()

    if (existing) {
      return Promise.resolve(existing)
    }

    return new Promise(resolve => {
      let eip6963Provider: NeoLineEthereumProvider | null = null

      const done = (provider: NeoLineEthereumProvider | null) => {
        clearTimeout(timeoutId)
        window.removeEventListener('eip6963:announceProvider', onAnnounce)
        resolve(provider)
      }

      const onAnnounce = (event: Event) => {
        const detail = (event as CustomEvent).detail

        if (detail?.provider?.isNEOLine && detail.provider.request) {
          eip6963Provider = detail.provider
          done(eip6963Provider)
        }
      }

      window.addEventListener('eip6963:announceProvider', onAnnounce)
      window.dispatchEvent(new Event('eip6963:requestProvider'))

      const timeoutId = setTimeout(() => {
        done(eip6963Provider ?? resolveNeoLineEthereumProvider())
      }, timeoutMs)
    })
  }
}
